/**
 * Area element map layer data (AST-96)
 *
 * Pure helpers that turn a project's saved area elements (AST-87) into what
 * the map draws: one marker per active element, coloured by section, plus a
 * bounding box so the map can fit them when the project has no point.
 */

// Distinct on the satellite basemap and on each other. Colours are [r, g, b].
// AST-100 (Mark): structure markers are black with an orange line, not orange
// with a white one. `fill` is the marker dot; `shapeFill` is the polygon/line
// colour for elements drawn from KMZ geometry -- they stay section-coloured,
// since a black-filled yard or road would read as a hole in the basemap.
export const SECTION_STYLES = {
  structures: { label: 'Structures', fill: [0, 0, 0], shapeFill: [236, 104, 37], radius: 7 },
  yards: { label: 'Project/Laydown Yards', fill: [66, 165, 245], shapeFill: [66, 165, 245], radius: 9 },
  other: { label: 'Other', fill: [189, 189, 189], shapeFill: [189, 189, 189], radius: 5 },
};

// asseTracOrange -- the outline on every area-element marker (AST-100).
export const AREA_ELEMENT_LINE = [236, 104, 37];

const isCoord = (lat, lng) => typeof lat === 'number' && typeof lng === 'number'
  && Number.isFinite(lat) && Number.isFinite(lng)
  && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

/**
 * Markers for the map. Drops retired elements, unknown sections and bad
 * coordinates. Draw order: Other underneath, then Yards, Structures on top,
 * so the elements people care most about stay clickable.
 */
export function areaElementMarkers(elements) {
  const order = { other: 0, yards: 1, structures: 2 };
  return (elements || [])
    .filter((e) => e && !e.retired && SECTION_STYLES[e.section] && isCoord(e.lat, e.lng))
    .map((e) => ({
      id: e.id,
      name: e.name,
      section: e.section,
      sectionLabel: SECTION_STYLES[e.section].label,
      position: [e.lng, e.lat],
      fill: SECTION_STYLES[e.section].fill,
      radius: SECTION_STYLES[e.section].radius,
    }))
    .sort((a, b) => order[a.section] - order[b.section]);
}

/**
 * Shapes for the map (AST-96): every polygon and line from active elements'
 * stored GeoJSON, flattened so each item is one ring-set or one path.
 *   { polygons: [{ id, name, sectionLabel, polygon: [[ [lng,lat], ... ], ...holes], fill, line }],
 *     paths:    [{ id, name, sectionLabel, path: [[lng,lat], ...], line }] }
 * Points stay markers (areaElementMarkers). Elements with no stored shape
 * contribute nothing here -- they're still drawn as their marker.
 */
export function areaElementShapes(elements) {
  const polygons = [];
  const paths = [];
  for (const e of elements || []) {
    if (!e || e.retired || !SECTION_STYLES[e.section] || !e.geometry) continue;
    const st = SECTION_STYLES[e.section];
    // Shapes keep the section colour (shapeFill), not the marker's black --
    // see SECTION_STYLES.
    const base = { id: e.id, name: e.name, section: e.section, sectionLabel: st.label, fill: st.shapeFill, line: st.shapeFill };
    const g = e.geometry;
    const coords = g.coordinates;
    if (!Array.isArray(coords)) continue;
    if (g.type === 'Polygon') polygons.push({ ...base, polygon: coords });
    else if (g.type === 'MultiPolygon') coords.forEach((poly, i) => polygons.push({ ...base, id: `${e.id}:${i}`, elementId: e.id, polygon: poly }));
    else if (g.type === 'LineString') paths.push({ ...base, path: coords });
    else if (g.type === 'MultiLineString') coords.forEach((line, i) => paths.push({ ...base, id: `${e.id}:${i}`, elementId: e.id, path: line }));
  }
  return { polygons, paths };
}

/**
 * Elements that are drawn as a shape don't also need a dot at their centre --
 * it sits in the middle of a yard or a road and reads as a separate thing.
 * Keep markers only for points and for elements with no stored shape.
 */
export function markersWithoutShapes(markers, elements) {
  const shaped = new Set((elements || [])
    .filter((e) => e?.geometry && /Polygon|LineString/.test(e.geometry.type))
    .map((e) => e.id));
  return markers.filter((m) => !shaped.has(m.id));
}

/**
 * [[minLng, minLat], [maxLng, maxLat]] around the markers -- and, when given,
 * every vertex of the shapes, so a long road isn't cut off at the edge of the
 * fitted view. null if there's nothing.
 */
export function markersBounds(markers, shapes = null) {
  let minLng = Infinity; let minLat = Infinity; let maxLng = -Infinity; let maxLat = -Infinity;
  const add = ([lng, lat]) => {
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  };
  for (const m of markers || []) add(m.position);
  for (const p of shapes?.polygons || []) p.polygon.forEach((ring) => ring.forEach(add));
  for (const p of shapes?.paths || []) p.path.forEach(add);
  if (minLng === Infinity) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
}

/**
 * A viewport that fits the bounds. Uses WebMercatorViewport.fitBounds when the
 * map size is known; otherwise (first paint) a span-based zoom. Single points
 * and tight clusters are capped at zoom 15 so it doesn't land at street level.
 */
export function viewportForBounds(bounds, { width, height, WebMercatorViewport } = {}) {
  if (!bounds) return null;
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const center = { latitude: (minLat + maxLat) / 2, longitude: (minLng + maxLng) / 2 };
  const span = Math.max(maxLat - minLat, maxLng - minLng);
  if (span === 0) return { ...center, zoom: 15 };

  if (WebMercatorViewport && width > 0 && height > 0) {
    try {
      const vp = new WebMercatorViewport({ width, height }).fitBounds(bounds, { padding: Math.min(60, Math.floor(Math.min(width, height) / 6)) });
      return { latitude: vp.latitude, longitude: vp.longitude, zoom: Math.min(vp.zoom, 15) };
    } catch {
      // fall through to the span table
    }
  }
  let zoom = 15;
  if (span > 2) zoom = 7;
  else if (span > 1) zoom = 8;
  else if (span > 0.5) zoom = 9;
  else if (span > 0.2) zoom = 10;
  else if (span > 0.1) zoom = 11;
  else if (span > 0.05) zoom = 12;
  else if (span > 0.02) zoom = 13;
  else if (span > 0.01) zoom = 14;
  return { ...center, zoom };
}
